// Integrations catalog (arc 101 slice 03) — software-focused click-to-connect
// connectors. An integration is account-scoped non-secret `config` (org names,
// buckets, channels) plus an optional vault reference (`secretRef`) to the sealed
// credential in the `secrets` table. The plaintext credential never lives on this
// entity — it is envelope-encrypted at rest and injected server-side, never
// returned to clients or task text.
//
// Two families, one row shape:
//   BESPOKE      `sentry | slack | s3` — hand-written config/secret shapes,
//                because a dedicated consumer reads each one.
//   TOOL SERVICE `tool_service:<slug>` (arc 101 slice 07) — everything the
//                connector needs comes from ONE `ToolServiceDescriptor`
//                (`tool-service.ts`). The kind, the connect form
//                (`CONNECTOR_FIELDS`), and the connect payload's Zod shapes are
//                all DERIVED here, so a new service is a registry entry plus a
//                locale key — no enum edit, no union member, no form change.

import { z } from 'zod';
import { vetPublicHttpUrl } from '../value-objects/host-vetting';
import { AccountIdSchema, IntegrationIdSchema } from '../value-objects/ids';
import { ArtifactExpiresInSchema } from './artifact';
import {
  TOOL_SERVICE_DESCRIPTORS,
  TOOL_SERVICE_HOSTNAME_MAX,
  TOOL_SERVICE_HOSTNAME_RE,
  TOOL_SERVICE_SLUGS,
  type ToolServiceDescriptor,
  type ToolServiceField,
  type ToolServiceSlug,
  toolServiceDescriptor,
  toolServiceFieldsByGroup,
} from './tool-service';

/**
 * The BESPOKE connectors — each has hand-written config/secret shapes because a
 * dedicated consumer reads them (artifact storage, the Slack egress gateway,
 * Sentry context). Text + Zod-at-read in the DB (no pgEnum, D3 stance).
 *
 * Everything else is a {@link ToolServiceDescriptor} under the `tool_service:`
 * family below — one registry entry, no schema edit.
 */
export const IntegrationKind = {
  /** Sentry error tracking — read issues/events into the agent's context. */
  Sentry: 'sentry',
  /** Slack notify — post agent notifications to a workspace channel. */
  Slack: 'slack',
  /** Custom S3-compatible object store (R2 / MinIO / AWS) — feeds artifact storage (slice 08). */
  S3: 's3',
} as const;
export type BaseIntegrationKind = (typeof IntegrationKind)[keyof typeof IntegrationKind];

const BASE_INTEGRATION_KINDS = [
  IntegrationKind.Sentry,
  IntegrationKind.Slack,
  IntegrationKind.S3,
] as const;

/**
 * Kind prefix for the tool-service family (arc 101 slice 07). The SLUG is part
 * of the kind, not a second column: `integrations` is unique on
 * `(account_id, kind)`, so a bare `tool_service` kind would cap an account at
 * ONE connected service. Encoding the slug keeps every existing surface —
 * upsert, `DELETE /v1/integrations/:kind`, the MCP `integration` verb — working
 * unchanged, with no migration.
 */
export const TOOL_SERVICE_KIND_PREFIX = 'tool_service:';

/** A tool-service connector kind, e.g. `tool_service:ui-debugger`. */
export type ToolServiceIntegrationKind = `${typeof TOOL_SERVICE_KIND_PREFIX}${ToolServiceSlug}`;

/** The connector kind a registered service connects as. */
export function toolServiceKind(slug: ToolServiceSlug): ToolServiceIntegrationKind {
  return `${TOOL_SERVICE_KIND_PREFIX}${slug}`;
}

/** Every tool-service kind, in registry order. */
export const TOOL_SERVICE_KINDS: readonly ToolServiceIntegrationKind[] =
  TOOL_SERVICE_SLUGS.map(toolServiceKind);

export const IntegrationKindSchema = z.enum([...BASE_INTEGRATION_KINDS, ...TOOL_SERVICE_KINDS]);
export type IntegrationKind = z.infer<typeof IntegrationKindSchema>;

/** Every connector kind, in catalog order — the dashboard iterates this. */
export const INTEGRATION_KINDS: readonly IntegrationKind[] = IntegrationKindSchema.options;

/** Is this kind a descriptor-driven tool service (vs. a bespoke connector)? */
export function isToolServiceKind(kind: IntegrationKind): kind is ToolServiceIntegrationKind {
  return kind.startsWith(TOOL_SERVICE_KIND_PREFIX);
}

/** The service slug behind a tool-service kind; `null` for a bespoke connector. */
export function toolServiceSlugOf(kind: IntegrationKind): ToolServiceSlug | null {
  if (!isToolServiceKind(kind)) return null;
  return kind.slice(TOOL_SERVICE_KIND_PREFIX.length) as ToolServiceSlug;
}

/** The descriptor behind a connector kind; `null` for a bespoke connector. */
export function toolServiceDescriptorForKind(kind: IntegrationKind): ToolServiceDescriptor | null {
  const slug = toolServiceSlugOf(kind);
  return slug === null ? null : toolServiceDescriptor(slug);
}

export const IntegrationStatus = {
  /** Credential accepted; the connector is live. */
  Connected: 'connected',
  /** Last use failed (bad credential, unreachable endpoint) — surfaced for repair. */
  Error: 'error',
} as const;
export const IntegrationStatusSchema = z.enum(IntegrationStatus);
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;

// ─── per-connector config (non-secret) ───────────────────────────────────────
// Snake_case keys mirror the JSON the API accepts (cf. `base_url`, `spend_cap_usd`).
// These validate + normalize the `config` jsonb stored on the row.

export const SentryConfigSchema = z
  .object({
    /** Sentry org slug, e.g. `acme`. */
    org: z.string().min(1),
    /** Optional project slug to scope reads to a single project. */
    project: z.string().min(1).optional(),
  })
  .strict();

export const SlackConfigSchema = z
  .object({
    /** Default channel notifications post to, e.g. `#dev-alerts`. Optional — the bot can be told per-message. */
    default_channel: z.string().min(1).optional(),
  })
  .strict();

/**
 * The BYO-S3 endpoint. `z.url()` is not enough on its own: this URL is dialed
 * FROM our cluster, with our network position, on the account's behalf — so a
 * bare URL check accepts `http://169.254.169.254/` (cloud metadata),
 * `http://postgres-rw.postgres.svc.cluster.local`, and every private range, and
 * any account that can connect an integration has an SSRF primitive.
 *
 * Vetted through the SAME gate as a BYOK `base_url` ({@link vetPublicHttpUrl}) —
 * one SSRF posture for every outbound the platform makes on a customer's behalf,
 * because two gates that disagree are worse than one and the weaker copy is the
 * one that gets used. Enforced HERE, on the schema, so it binds at the write
 * seam every transport shares (`POST /v1/integrations` and the OAuth connect
 * flow both parse {@link ConnectIntegrationSchema}) rather than at fetch time —
 * a stored forbidden endpoint would be a row that reads `connected` and fails
 * forever.
 *
 * This is the LITERAL half only. A public name that RESOLVES private (DNS
 * rebind) passes it; the dial-time half is `endpointHostGate` in
 * `@developerz/storage`, installed on every client built with
 * `endpointTrust: { kind: 'customer' }`.
 */
const S3EndpointSchema = z.string().superRefine((raw, ctx) => {
  const verdict = vetPublicHttpUrl(raw, 'endpoint');
  if (!verdict.ok) ctx.addIssue({ code: 'custom', message: verdict.reason });
});

export const S3ConfigSchema = z
  .object({
    /** S3-compatible endpoint, e.g. `https://<acct>.r2.cloudflarestorage.com`. */
    endpoint: S3EndpointSchema,
    /** Bucket name. */
    bucket: z.string().min(1),
    /** Region. `auto` suits R2 / most S3-compatible stores. */
    region: z.string().min(1).default('auto'),
    /** Path-style addressing (required by R2 / MinIO). */
    force_path_style: z.boolean().default(true),
    /**
     * Account-level default artifact expiry (arc 101 slice 08): seconds, or
     * `'never'`. Applies to every artifact stored in THIS bucket unless a put
     * overrides it per-item (`expires_in`). Omitted ⇒ the platform default (7d).
     * Ignored on the platform R2 fallback (that tier is a hard 7d, no override).
     */
    artifact_expiry: ArtifactExpiresInSchema.optional(),
  })
  .strict();

// ─── per-connector secret (plaintext to seal) ────────────────────────────────
// The route validates one of these, then envelope-seals `JSON.stringify(secret)`
// into the account's vault. The plaintext never reaches the DB, an event, or a
// client response.

export const SentrySecretSchema = z.object({ auth_token: z.string().min(1) }).strict();
export const SlackSecretSchema = z.object({ bot_token: z.string().min(1) }).strict();
export const S3SecretSchema = z
  .object({ access_key_id: z.string().min(1), secret_access_key: z.string().min(1) })
  .strict();

// ─── connect payload — one shape per kind, discriminated ─────────────────────
// The three bespoke connectors carry hand-written shapes; every tool service
// DERIVES its two shapes from its descriptor's fields, so a new service adds a
// union member without a schema edit.

/**
 * A declared field format → the string schema that enforces it. The connect route
 * is the LAST place a tool-service value is a human's typing rather than a box env
 * var, so a format declared on the descriptor has to bite here or nowhere.
 */
const FORMAT_SCHEMA: Record<NonNullable<ToolServiceField['format']>, z.ZodString> = {
  hostname: z
    .string()
    .max(TOOL_SERVICE_HOSTNAME_MAX)
    .regex(TOOL_SERVICE_HOSTNAME_RE, 'must be a bare hostname — no scheme, path, or credentials'),
};

/** One field → its Zod shape. Non-secret defaults are applied on parse. */
function fieldSchema(field: ToolServiceField): z.ZodType {
  const value = (field.format ? FORMAT_SCHEMA[field.format] : z.string()).min(1);
  if (field.default !== undefined) return value.default(field.default);
  return field.required ? value : value.optional();
}

function groupSchema(fields: readonly ToolServiceField[]) {
  const shape: Record<string, z.ZodType> = {};
  for (const field of fields) shape[field.name] = fieldSchema(field);
  // `.strict()` like the bespoke shapes: an unknown key is a typo or an
  // exfiltration attempt, never a value we quietly store on the row.
  return z.object(shape).strict();
}

function toolServiceConnectSchema(descriptor: (typeof TOOL_SERVICE_DESCRIPTORS)[number]) {
  const groups = toolServiceFieldsByGroup(descriptor);
  return z.object({
    kind: z.literal(toolServiceKind(descriptor.slug)),
    config: groupSchema(groups.config),
    secret: groupSchema(groups.secret),
  });
}

export const ConnectIntegrationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(IntegrationKind.Sentry),
    config: SentryConfigSchema,
    secret: SentrySecretSchema,
  }),
  z.object({
    kind: z.literal(IntegrationKind.Slack),
    config: SlackConfigSchema,
    secret: SlackSecretSchema,
  }),
  z.object({
    kind: z.literal(IntegrationKind.S3),
    config: S3ConfigSchema,
    secret: S3SecretSchema,
  }),
  ...TOOL_SERVICE_DESCRIPTORS.map(toolServiceConnectSchema),
]);
export type ConnectIntegrationInput = z.infer<typeof ConnectIntegrationSchema>;

// ─── UI field descriptors ────────────────────────────────────────────────────
// Pure metadata (no display strings — the dashboard owns i18n): what fields a
// connector's connect form renders, whether each is a secret (password input),
// and which group it lands in (`config` on the row vs. `secret` into the vault).

export type ConnectorFieldGroup = 'config' | 'secret';

export type ConnectorField = {
  /**
   * Field name — the JSON key in `config` / `secret`, and the label key the
   * dashboard renders (`integrations.field.<name>`). Unique WITHIN a connector;
   * two connectors sharing a name deliberately share one label, which is what
   * keeps a new tool service down to a descriptor entry plus its own name key.
   */
  readonly name: string;
  readonly group: ConnectorFieldGroup;
  /** Render as a password input and never echo — every `secret`-group field is one. */
  readonly secret: boolean;
  readonly required: boolean;
};

/** The bespoke connectors' fields — hand-written, matching their hand-written shapes. */
const BASE_CONNECTOR_FIELDS: Record<BaseIntegrationKind, readonly ConnectorField[]> = {
  [IntegrationKind.Sentry]: [
    { name: 'org', group: 'config', secret: false, required: true },
    { name: 'project', group: 'config', secret: false, required: false },
    { name: 'auth_token', group: 'secret', secret: true, required: true },
  ],
  [IntegrationKind.Slack]: [
    { name: 'default_channel', group: 'config', secret: false, required: false },
    { name: 'bot_token', group: 'secret', secret: true, required: true },
  ],
  [IntegrationKind.S3]: [
    { name: 'endpoint', group: 'config', secret: false, required: true },
    { name: 'bucket', group: 'config', secret: false, required: true },
    { name: 'region', group: 'config', secret: false, required: false },
    { name: 'access_key_id', group: 'secret', secret: true, required: true },
    { name: 'secret_access_key', group: 'secret', secret: true, required: true },
  ],
};

/** A descriptor's fields as connect-form fields — the group IS the secret flag. */
function descriptorConnectorFields(descriptor: ToolServiceDescriptor): readonly ConnectorField[] {
  return descriptor.fields.map((field) => ({
    name: field.name,
    group: field.secret ? 'secret' : 'config',
    secret: field.secret,
    required: field.required,
  }));
}

/**
 * The connect-form fields per connector, in render order. The tool-service half
 * is DERIVED from {@link TOOL_SERVICE_DESCRIPTORS}, so the dashboard's connect
 * form stays generated: `ConnectIntegrationModal` reads this record and needs no
 * edit per connector.
 */
export const CONNECTOR_FIELDS: Record<IntegrationKind, readonly ConnectorField[]> =
  buildConnectorFields();

function buildConnectorFields(): Record<IntegrationKind, readonly ConnectorField[]> {
  const fields: Partial<Record<IntegrationKind, readonly ConnectorField[]>> = {
    ...BASE_CONNECTOR_FIELDS,
  };
  for (const descriptor of TOOL_SERVICE_DESCRIPTORS) {
    fields[toolServiceKind(descriptor.slug)] = descriptorConnectorFields(descriptor);
  }
  // Total by construction: every `INTEGRATION_KINDS` member is a base kind or a
  // registry slug, and both loops above are exhaustive over those two sources.
  return fields as Record<IntegrationKind, readonly ConnectorField[]>;
}

// ─── the stored entity ───────────────────────────────────────────────────────

export const IntegrationSchema = z.object({
  id: IntegrationIdSchema,
  accountId: AccountIdSchema,
  kind: IntegrationKindSchema,
  /** Non-secret connector config; shape is validated per-kind at connect time. */
  config: z.record(z.string(), z.unknown()),
  /** Vault ref: the `secrets.name` holding this connector's sealed credential. Null when keyless. */
  secretRef: z.string().nullable(),
  status: IntegrationStatusSchema,
  lastError: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type Integration = z.infer<typeof IntegrationSchema>;

/**
 * Vault secret name for a connector's credential. Namespaced so it can't collide
 * with a user-defined `.maintainer.yml` secret handle.
 *
 * The name is `integration:<handle>`, where the handle is the kind for a
 * bespoke connector and the SLUG for a tool service — NOT the full
 * `tool_service:<slug>` kind. A policy `*_ref` accepts at most one `ns:name`
 * colon (`RefName`, @developerz/policy), so `integration:tool_service:x` would
 * be a vault row no `.maintainer.yml` could reference. Slugs are held disjoint
 * from the base kinds by `tool-service.test.ts`, so the handle stays unique.
 */
export function integrationSecretName(kind: IntegrationKind): string {
  return `integration:${toolServiceSlugOf(kind) ?? kind}`;
}
